import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess, type Paginated } from '../../lib/api';
import { StaffSelect } from '../../components/StaffSelect';

interface ComplaintRow {
  id: string;
  ticketNumber: string;
  type: string;
  subject: string;
  description: string;
  status: 'NEW' | 'IN_REVIEW' | 'AWAITING_REPLY' | 'RESOLVED' | 'REJECTED' | 'ESCALATED';
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  reporterName: string;
  reporterEmail: string;
  reporterPhone: string | null;
  evidence: Array<{ url: string; name?: string }>;
  internalNotes: string | null;
  response: string | null;
  assignedTo: { id: string; name: string } | null;
  reporter: { id: string; name: string; email: string } | null;
  againstUser: { id: string; name: string } | null;
  slaDeadline: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

const STATUS: Record<ComplaintRow['status'], { label: string; color: 'amber' | 'navy' | 'gold' | 'green' | 'red' }> = {
  NEW:           { label: 'جديدة',            color: 'amber' },
  IN_REVIEW:     { label: 'قيد المراجعة',     color: 'navy' },
  AWAITING_REPLY: { label: 'بانتظار المُشتكي', color: 'gold' },
  RESOLVED:      { label: 'مُغلَقة بحل',      color: 'green' },
  REJECTED:      { label: 'مرفوضة',           color: 'red' },
  ESCALATED:     { label: 'مُحالة',           color: 'navy' },
};

const PRIORITY: Record<ComplaintRow['priority'], { label: string; color: 'green' | 'navy' | 'amber' | 'red' }> = {
  LOW: { label: 'منخفضة', color: 'green' }, NORMAL: { label: 'عادية', color: 'navy' },
  HIGH: { label: 'مرتفعة', color: 'amber' }, URGENT: { label: 'عاجلة', color: 'red' },
};

const TYPE_LABEL: Record<string, string> = {
  PROPERTY_AD: '🏠 إعلان', AUCTION: '🔨 مزاد', BROKER: '🧑‍💼 مسوّق',
  PAYMENT: '💳 دفع', HARASSMENT: '⚠️ تحرّش', PRIVACY: '🔒 خصوصية',
  COPYRIGHT: '© ملكية', OTHER: '📄 أخرى',
};

export function AdminComplaintsPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ status: '', priority: '', type: '', q: '' });
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'complaints', filters],
    queryFn: () => {
      const q: Record<string, string | number> = { limit: 50 };
      if (filters.status)   q.status = filters.status;
      if (filters.priority) q.priority = filters.priority;
      if (filters.type)     q.type = filters.type;
      if (filters.q)        q.q = filters.q;
      return api.get<Paginated<ComplaintRow>>('/api/admin/complaints', { query: q });
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/complaints/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'complaints'] }),
  });

  // 📊 ملخص حسب الحالة
  const stats = (data?.data ?? []).reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-navy-dark">📣 الشكاوى</h1>
          <p className="text-sm text-gray-500 mt-1">معالجة شكاوى المستخدمين وفق متطلبات الهيئة العامة للعقار (REGA).</p>
        </div>
      </div>

      {/* 📊 ملخص */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-5">
        {(['NEW', 'IN_REVIEW', 'AWAITING_REPLY', 'RESOLVED', 'REJECTED', 'ESCALATED'] as const).map((s) => (
          <Card key={s}><CardBody className="text-center !p-3">
            <div className="text-2xl font-black text-navy-dark">{stats[s] ?? 0}</div>
            <div className="text-xs text-gray-500">{STATUS[s].label}</div>
          </CardBody></Card>
        ))}
      </div>

      <Card className="mb-4"><CardBody>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input placeholder="🔍 بحث (رقم التذكرة، الاسم، الإيميل...)" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
            <option value="">كل الحالات</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
            <option value="">كل الأولويات</option>
            {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
            <option value="">كل الأنواع</option>
            {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </CardBody></Card>

      {isLoading ? (
        <div className="text-gray-500">⟳ جاري التحميل…</div>
      ) : !data?.data?.length ? (
        <Card><CardBody><div className="text-center py-12 text-gray-500"><div className="text-5xl mb-3">📭</div>لا توجد شكاوى</div></CardBody></Card>
      ) : (
        <div className="space-y-3">
          {data.data.map((r) => {
            const overdue = r.slaDeadline && !r.resolvedAt && new Date(r.slaDeadline) < new Date();
            return (
              <Card key={r.id} className={overdue ? 'border-red-300' : ''}>
                <CardBody>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[250px]">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs text-gray-400 font-mono">{r.ticketNumber}</span>
                        <Badge color={STATUS[r.status].color}>{STATUS[r.status].label}</Badge>
                        <Badge color={PRIORITY[r.priority].color}>{PRIORITY[r.priority].label}</Badge>
                        <span className="text-xs text-gray-500">{TYPE_LABEL[r.type] ?? r.type}</span>
                        {overdue && <Badge color="red">⏰ متأخّرة عن الـ SLA</Badge>}
                      </div>
                      <h3 className="font-black text-navy-dark mb-1">{r.subject}</h3>
                      <p className="text-sm text-gray-600 line-clamp-2">{r.description}</p>
                      <div className="text-xs text-gray-500 mt-2 grid grid-cols-2 gap-1">
                        <div>📧 <span dir="ltr">{r.reporterEmail}</span></div>
                        <div>🧑 {r.reporterName}</div>
                        {r.assignedTo && <div>مسند لـ: <b>{r.assignedTo.name}</b></div>}
                        <div>تاريخ: {new Date(r.createdAt).toLocaleDateString('ar-SA')}</div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="primary" onClick={() => setOpenId(r.id)}>إدارة</Button>
                      <Button size="sm" variant="ghost" onClick={() => { if (confirm('حذف الشكوى نهائياً؟')) deleteM.mutate(r.id); }}>🗑</Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      {openId && data?.data && (
        <ComplaintManageModal
          row={data.data.find((r) => r.id === openId)!}
          onClose={() => setOpenId(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'complaints'] })}
        />
      )}
    </div>
  );
}

function ComplaintManageModal({ row, onClose, onSaved }: { row: ComplaintRow; onClose: () => void; onSaved: () => void }) {
  const [tab, setTab] = useState<'details' | 'manage' | 'respond' | 'history'>('details');
  const [meta, setMeta] = useState({
    status: row.status,
    priority: row.priority,
    assignedToId: row.assignedTo?.id ?? '',
    internalNotes: row.internalNotes ?? '',
  });
  const [response, setResponse] = useState('');
  const [closeStatus, setCloseStatus] = useState<'RESOLVED' | 'REJECTED'>('RESOLVED');
  const [err, setErr] = useState('');


  const updateM = useMutation({
    mutationFn: () => api.put(`/api/admin/complaints/${row.id}`, {
      ...meta,
      assignedToId: meta.assignedToId || null,
      internalNotes: meta.internalNotes || null,
    }),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });
  const respondM = useMutation({
    mutationFn: () => api.post(`/api/admin/complaints/${row.id}/respond`, { response, closeStatus }),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-xl font-black text-navy-dark">{row.subject}</h2>
              <div className="text-xs text-gray-500 mt-1 font-mono">{row.ticketNumber}</div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
          </div>
          <div className="flex gap-2 border-b -mb-5">
            <Tab active={tab === 'details'} onClick={() => setTab('details')}>📋 التفاصيل</Tab>
            <Tab active={tab === 'manage'}  onClick={() => setTab('manage')}>🛠️ الإدارة</Tab>
            <Tab active={tab === 'respond'} onClick={() => setTab('respond')} disabled={!!row.resolvedAt}>✉️ الردّ الرسمي</Tab>
            <Tab active={tab === 'history'} onClick={() => setTab('history')}>📜 السجل</Tab>
          </div>
        </div>

        <div className="p-5 space-y-3">
          {tab === 'details' && (
            <>
              <Row label="النوع" value={TYPE_LABEL[row.type] ?? row.type} />
              <Row label="الحالة" value={STATUS[row.status].label} />
              <Row label="الأولوية" value={PRIORITY[row.priority].label} />
              <Row label="المُشتكي" value={`${row.reporterName} — ${row.reporterEmail}${row.reporterPhone ? ` — ${row.reporterPhone}` : ''}`} />
              {row.againstUser && <Row label="المُشتَكى عليه" value={row.againstUser.name} />}
              <div>
                <div className="text-xs text-gray-500 mb-1">الوصف</div>
                <div className="bg-gray-50 rounded-lg p-3 text-sm whitespace-pre-wrap">{row.description}</div>
              </div>
              {row.evidence && row.evidence.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 mb-2">الأدلة ({row.evidence.length})</div>
                  <div className="grid grid-cols-3 gap-2">
                    {row.evidence.map((e, i) => (
                      <a key={i} href={e.url} target="_blank" rel="noreferrer" className="block aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        <img src={e.url} alt={e.name ?? `evidence-${i}`} className="w-full h-full object-cover hover:scale-105 transition" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {row.response && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">الردّ الرسمي السابق</div>
                  <div className="bg-navy-soft/30 border-r-4 border-gold rounded-lg p-3 text-sm whitespace-pre-wrap">{row.response}</div>
                </div>
              )}
            </>
          )}

          {tab === 'manage' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-bold text-ink mb-1">الحالة</label>
                  <select value={meta.status} onChange={(e) => setMeta({ ...meta, status: e.target.value as ComplaintRow['status'] })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-ink mb-1">الأولوية</label>
                  <select value={meta.priority} onChange={(e) => setMeta({ ...meta, priority: e.target.value as ComplaintRow['priority'] })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
                    {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
              </div>
              <StaffSelect value={meta.assignedToId} onChange={(id) => setMeta({ ...meta, assignedToId: id })} />
              <div>
                <label className="block text-sm font-bold text-ink mb-1">ملاحظات داخلية (لا تُرسَل للمُشتكي)</label>
                <textarea value={meta.internalNotes} onChange={(e) => setMeta({ ...meta, internalNotes: e.target.value })} rows={4} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm" />
              </div>
            </>
          )}

          {tab === 'respond' && (
            <>
              {row.resolvedAt ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                  ✓ تم إغلاق هذه الشكوى مسبقاً.
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-bold text-ink mb-1">نص الردّ الرسمي *</label>
                    <textarea
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      rows={8}
                      className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
                      placeholder="سيُرسَل للمُشتكي بالإيميل ويُغلَق ملف الشكوى."
                    />
                    <p className="text-xs text-gray-500 mt-1">١٠ أحرف فأكثر.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-ink mb-1">قرار الإغلاق</label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setCloseStatus('RESOLVED')} className={`flex-1 p-3 rounded-xl border-2 font-bold transition ${closeStatus === 'RESOLVED' ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-200 bg-white text-gray-700'}`}>✅ مُغلَقة بحل</button>
                      <button type="button" onClick={() => setCloseStatus('REJECTED')} className={`flex-1 p-3 rounded-xl border-2 font-bold transition ${closeStatus === 'REJECTED' ? 'border-red-500 bg-red-50 text-red-800' : 'border-gray-200 bg-white text-gray-700'}`}>⛔ مرفوضة</button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {tab === 'history' && <ComplaintTimeline complaintId={row.id} />}

          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
        </div>

        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          {tab === 'manage' && <Button variant="primary" onClick={() => updateM.mutate()} loading={updateM.isPending}>💾 حفظ</Button>}
          {tab === 'respond' && !row.resolvedAt && (
            <Button variant="primary" onClick={() => { if (response.trim().length < 10) { setErr('الردّ قصير جداً'); return; } respondM.mutate(); }} loading={respondM.isPending}>📤 إرسال الردّ وإغلاق</Button>
          )}
        </div>
      </div>
    </div>
  );
}

// 📜 Timeline من سجل التدقيق
interface AuditEntry {
  id: string;
  action: string;
  oldData: Record<string, unknown> | null;
  newData: Record<string, unknown> | null;
  createdAt: string;
  user: { id: string; name: string; userNumber: number } | null;
}

const ACTION_LABEL: Record<string, string> = {
  update_complaint: '🛠️ تعديل',
  respond_complaint: '✉️ ردّ رسمي',
  delete_complaint: '🗑️ حذف',
};

const STATUS_AR: Record<string, string> = {
  NEW: 'جديدة', IN_REVIEW: 'قيد المراجعة', AWAITING_REPLY: 'بانتظار المُشتكي',
  RESOLVED: 'مُغلَقة بحل', REJECTED: 'مرفوضة', ESCALATED: 'مُحالة',
};

const PRIORITY_AR: Record<string, string> = {
  LOW: 'منخفضة', NORMAL: 'عادية', HIGH: 'مرتفعة', URGENT: 'عاجلة',
};

function ComplaintTimeline({ complaintId }: { complaintId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['complaint', complaintId, 'history'],
    queryFn: () => api.get<ApiSuccess<AuditEntry[]>>(`/api/admin/complaints/${complaintId}/history`),
  });

  if (isLoading) return <div className="text-center text-gray-500 py-8">⟳ جاري التحميل…</div>;
  if (!data?.data?.length) return <div className="text-center text-gray-500 py-8">لا توجد تعديلات مسجلة</div>;

  return (
    <div className="space-y-3">
      {data.data.map((e) => {
        const summary: string[] = [];
        if (e.newData) {
          const n = e.newData as Record<string, unknown>;
          if (n.status)        summary.push(`الحالة → ${STATUS_AR[String(n.status)] ?? String(n.status)}`);
          if (n.priority)      summary.push(`الأولوية → ${PRIORITY_AR[String(n.priority)] ?? String(n.priority)}`);
          if (n.assignedToId)  summary.push(`تم الإسناد`);
          if (n.assignedToId === null) summary.push(`أُلغي الإسناد`);
          if (n.internalNotes !== undefined && n.internalNotes !== null) summary.push(`تعديل ملاحظات داخلية`);
          if (n.closeStatus)   summary.push(`الإغلاق → ${STATUS_AR[String(n.closeStatus)] ?? String(n.closeStatus)}`);
        }
        return (
          <div key={e.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-navy-dark">{ACTION_LABEL[e.action] ?? e.action}</span>
                {e.user && (
                  <span className="text-xs text-gray-500">
                    بواسطة <b>{e.user.name}</b>
                    <span className="font-mono mr-1">#{e.user.userNumber}</span>
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-400">{new Date(e.createdAt).toLocaleString('ar-SA')}</span>
            </div>
            {summary.length > 0 && (
              <div className="mt-2 text-xs text-gray-700 leading-relaxed">
                {summary.map((s, i) => (
                  <span key={i} className="inline-block bg-white border border-gray-200 rounded-full px-2 py-0.5 mr-1 mb-1">{s}</span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Tab({ active, onClick, disabled, children }: { active: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button onClick={onClick} disabled={disabled} className={`px-4 py-2 text-sm font-bold transition border-b-2 -mb-px whitespace-nowrap ${
      active ? 'border-gold text-navy-dark' : 'border-transparent text-ink-muted hover:text-navy-dark'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>{children}</button>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3 text-sm">
      <div className="text-xs text-gray-500 mb-0.5">{label}</div>
      <div className="font-bold text-navy-dark">{value}</div>
    </div>
  );
}
